import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

import { Modal } from '../../custom-essentials';
import FlagsBodyFooter from './FlagsBF';
import { checkResponse } from '../../form';

import {
    fetchFlagsIds,
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchAdminUsersIds,
    fetchAdminUsersAll,
    fetchStudentsAll,
    createFlag,
    updateFlag,
    deleteFlag,
} from '../../../actions';

const initDate = new Date();
initDate.setHours(0, 0, 0, 0);

// Detect portal from domain (case-insensitive)
const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
const isReadingPlex = hostname.includes('readingplex');

function FlagModal(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const [showModal, setShowModal] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [flag, setFlag] = useState({});
    const [centerOptions, setCenterOptions] = useState([]);
    const [studentOptions, setStudentOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]); // For ReadingPlex: students + employees
    const [employeeOptions, setEmployeeOptions] = useState([]); // For Kanban: assigned employees multi-select

    const { mode, selectedFlag, onSubmitCallback, fetchFlagsIds, fetchMpCentersAll, fetchRpCentersAll,
        fetchAdminUsersIds, fetchAdminUsersAll, fetchStudentsAll, createFlag, updateFlag, deleteFlag } = props;

    useEffect(() => {
        async function init() {
            try {
                if (isReadingPlex) {
                    // ReadingPlex: User dropdown should show employees/admins only (no students)
                    console.log('[FLAG MODAL] Fetching ReadingPlex data...');
                    let employeesRes, studentsRes;
                    try {
                        [employeesRes, studentsRes] = await Promise.all([
                            fetchAdminUsersAll(),
                            fetchStudentsAll() // Fetch students for the Students dropdown
                        ]);
                    } catch (error) {
                        console.error('[FLAG MODAL] Error fetching data:', error);
                        employeesRes = employeesRes || { status: 500, data: [] };
                        studentsRes = studentsRes || { status: 500, data: [] };
                    }
                    
                    console.log('[FLAG MODAL] Fetch results:', {
                        employeesRes: { status: employeesRes?.status, hasData: !!employeesRes?.data, dataLength: Array.isArray(employeesRes?.data) ? employeesRes.data.length : 'not array' },
                        studentsRes: { status: studentsRes?.status, hasData: !!studentsRes?.data, dataLength: Array.isArray(studentsRes?.data) ? studentsRes.data.length : 'not array', fullResponse: studentsRes }
                    });
                    
                    const newEmployees = Array.isArray(employeesRes?.data) ? employeesRes.data : [];
                    const newStudents = Array.isArray(studentsRes?.data) ? studentsRes.data : [];
                    
                    console.log('[FLAG MODAL] Parsed data:', {
                        employees: newEmployees.length,
                        students: newStudents.length,
                        studentsResStatus: studentsRes?.status,
                        studentsResData: studentsRes?.data ? (Array.isArray(studentsRes.data) ? `Array(${studentsRes.data.length})` : typeof studentsRes.data) : 'undefined'
                    });

                    // Filter ReadingPlex employees/admins
                    // NOTE: adminUsersAll should already be employees/admins, but we keep a safety filter.
                    const allowedPerms = new Set(['Root', 'Admin', 'Lead Instructor', 'Instructor', 'Employee']);
                    const filteredEmployees = newEmployees
                        .filter(e => {
                            if (selectedFlag?.user === e.id) return true; // Always include selected user
                            const perm = (e.rp_permissions || '').toString();
                            return !perm || allowedPerms.has(perm);
                        })
                        .map(ne => ({
                        value: ne.id,
                        label: `${ne.first_name} ${ne.last_name} (${ne.rp_permissions || 'Employee'})`,
                        obj: ne,
                        type: 'employee'
                    }));

                    // Employees-only for the User dropdown
                    const newUserOptions = [...filteredEmployees];

                    // Filter ReadingPlex students for the Students dropdown
                    const filteredStudents = newStudents
                        .filter(s => {
                            if (!s) return false;
                            return parseInt(s.rp_active) === 1 && parseInt(s.is_rp_student) === 1;
                        })
                        .map(s => ({
                            value: s.user_id,
                            label: `${s.first_name} ${s.last_name} (Student)`,
                            obj: s,
                            type: 'student'
                        }));

                    if (mode !== 'create') {
                        const flagRes = await fetchFlagsIds({ ids: [selectedFlag.id] });
                        const newFlag = flagRes.data?.[0] || {};

                        // Find the user (could be student or employee)
                        const flagUser = newUserOptions.find(u => u.value === newFlag.user);

                        const flagMeta = [];
                        if (newFlag.created_by) flagMeta.push(newFlag.created_by);
                        if (newFlag.updated_by) flagMeta.push(newFlag.updated_by);

                        const employeesMetaRes = await fetchAdminUsersIds({ ids: flagMeta });
                        const newEmployeesMeta = employeesMetaRes.data || [];

                        const cbInstructor = newEmployeesMeta.find(ni => ni.id === newFlag.created_by);
                        const ubInstructor = newEmployeesMeta.find(ni => ni.id === newFlag.updated_by);

                        newFlag.userName = flagUser ? flagUser.label :
                            `Unknown user (UID ${newFlag.user})`;
                        if (cbInstructor) {
                            newFlag.createdByName = cbInstructor ?
                                `${cbInstructor.first_name} ${cbInstructor.last_name}` :
                                `Unknown admin user (ID: ${newFlag.created_by})`;
                        }
                        if (ubInstructor) {
                            newFlag.updatedByName = ubInstructor ?
                                `${ubInstructor.first_name} ${ubInstructor.last_name}` :
                                `Unknown admin user (ID: ${newFlag.updated_by})`;
                        }

                        if (mounted.current) setFlag(newFlag);
                    }

                    // Fetch ReadingPlex centers
                    console.log('[FLAG MODAL] Fetching ReadingPlex centers...');
                    const centersRes = await fetchRpCentersAll();
                    console.log('[FLAG MODAL] Centers response:', {
                        status: centersRes?.status,
                        hasData: !!centersRes?.data,
                        dataType: Array.isArray(centersRes?.data) ? 'array' : typeof centersRes?.data,
                        dataLength: Array.isArray(centersRes?.data) ? centersRes.data.length : 'not array'
                    });
                    
                    const newCenters = Array.isArray(centersRes?.data) ? centersRes.data : [];
                    const newCenterOptions = newCenters.map(c => ({ value: parseInt(c.id), label: c.name }));
                    
                    console.log('[FLAG MODAL] Center options:', newCenterOptions.length, 'centers');

                    // Build employee options for multi-select (assigned employees)
                    // filteredEmployees is already in { value, label, obj } shape.
                    const employeeOptionsList = filteredEmployees.map(e => ({
                        value: e.value,
                        label: e.label,
                        obj: e.obj
                    }));

                    if (mounted.current) {
                        console.log('[FLAG MODAL] Setting state with options:', {
                            userOptions: newUserOptions.length,
                            studentOptions: filteredStudents.length,
                            centerOptions: newCenterOptions.length,
                            employeeOptions: employeeOptionsList.length
                        });
                        
                        setUserOptions(newUserOptions);
                        setStudentOptions(filteredStudents); // Actual students for the Students dropdown
                        setCenterOptions(newCenterOptions);
                        setEmployeeOptions(employeeOptionsList);
                        setLoaded(true);
                    }
                } else {
                    // MathPlex: Only students
                    const studentsRes = await fetchStudentsAll();
                    const newStudents = studentsRes.data || [];

                    // Filter MathPlex students
                    const newStudentOptions = newStudents.filter(s => {
                        if (selectedFlag?.student === s.user_id) return true; // Always include selected student
                        return parseInt(s.mp_active) === 1 && parseInt(s.is_mp_student) === 1;
                    }).map(ns => ({ value: ns.user_id, label: `${ns.first_name} ${ns.last_name}`, obj: ns }));

                    if (mode !== 'create') {
                        const flagRes = await fetchFlagsIds({ ids: [selectedFlag.id] });
                        const newFlag = flagRes.data?.[0] || {};
                        const flagStudent = newStudents.find(s => s.user_id === selectedFlag.student);

                        const flagMeta = [];
                        if (newFlag.created_by) flagMeta.push(newFlag.created_by);
                        if (newFlag.updated_by) flagMeta.push(newFlag.updated_by);

                        const employeesRes = await fetchAdminUsersIds({ ids: flagMeta });
                        const newEmployees = employeesRes.data || [];

                        const cbInstructor = newEmployees.find(ni => ni.id === newFlag.created_by);
                        const ubInstructor = newEmployees.find(ni => ni.id === newFlag.updated_by);

                        newFlag.studentName = flagStudent ? `${flagStudent.first_name} ${flagStudent.last_name}` :
                            `Unknown student (UID ${newFlag.student})`;
                        if (cbInstructor) {
                            newFlag.createdByName = cbInstructor ?
                                `${cbInstructor.first_name} ${cbInstructor.last_name}` :
                                `Unknown admin user (ID: ${newFlag.created_by})`;
                        }
                        if (ubInstructor) {
                            newFlag.updatedByName = ubInstructor ?
                                `${ubInstructor.first_name} ${ubInstructor.last_name}` :
                                `Unknown admin user (ID: ${newFlag.updated_by})`;
                        }

                        if (mounted.current) setFlag(newFlag);
                    }

                    // Fetch MathPlex centers
                    const centersRes = await fetchMpCentersAll();
                    const newCenters = centersRes.data || [];
                    const newCenterOptions = newCenters.map(c => ({ value: parseInt(c.id), label: c.name }));

                    if (mounted.current) {
                        setStudentOptions(newStudentOptions);
                        setCenterOptions(newCenterOptions);
                        setLoaded(true);
                    }
                }
            } catch (error) {
                console.error('[FLAG MODAL] Error loading data:', error);
                console.error('[FLAG MODAL] Error stack:', error.stack);
                if (mounted.current) {
                    // Set loaded to true anyway to show the form, even with errors
                    // Set empty arrays as fallback so dropdowns don't break
                    setUserOptions([]);
                    setStudentOptions([]);
                    setCenterOptions([]);
                    setEmployeeOptions([]);
                    setLoaded(true);
                }
            }
        };
        init();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleClose = useCallback((changes) => {
        if (changes !== true) changes = false;
        async function close() {
            await onSubmitCallback(changes);
            if (mounted.current) setShowModal(false);
        }
        close();
    }, [onSubmitCallback]);

    const handleSubmit = useCallback((values, actions) => {
        async function submit() {
            const { setStatus, setSubmitting } = actions;

            let response = { status: 999 };

            if (['create', 'edit'].includes(mode)) {
                let flagParams;

                if (isReadingPlex) {
                    // ReadingPlex structure: user, notes, followupNotes, center, priority, status
                    // Plus Kanban fields: projectName, taskName, dueDate, assignedEmployees, kanbanStatus, students
                    // Status and kanbanStatus are now the same (To Do, In Progress, Review, Done)
                    const statusValue = values.status?.value || 'To Do';
                    flagParams = {
                        user: values.user.value,
                        notes: values.notes || '',
                        followupNotes: values.followupNotes || '',
                        center: values.center.value,
                        priority: values.priority?.value || 'Low',
                        status: statusValue, // Now uses To Do, In Progress, Review, Done
                        // Kanban fields
                        projectName: values.projectName || '', // Project name text field instead of projectId
                        taskName: values.taskName || values.notes || '', // Use taskName if provided, fallback to notes
                        dueDate: values.dueDate ? values.dueDate : null, // DatePicker already returns 'YYYY-MM-DD' format
                        assignedEmployees: values.assignedEmployees && Array.isArray(values.assignedEmployees) && values.assignedEmployees.length > 0
                            ? values.assignedEmployees.map(e => e.value)
                            : null,
                        students: values.students && Array.isArray(values.students) && values.students.length > 0
                            ? values.students.map(s => s.value)
                            : null,
                        kanbanStatus: statusValue, // Same as status now
                    };
                    
                    // Debug logging for students field
                    console.log('[FlagModal] Submitting flag with students:', flagParams.students);
                    console.log('[FlagModal] values.students:', values.students);
                    console.log('[FlagModal] students array length:', values.students?.length);
                } else {
                    // MathPlex structure: student, tag, additionalNotes, adminNotes, center, status
                    flagParams = {
                        student: values.student.value,
                        tag: values.tag.value,
                        center: values.center.value,
                        additionalNotes: values.additionalNotes || '',
                        adminNotes: values.adminNotes || '',
                        status: values.status.value,
                    };
                }

                if (mode === 'create') {
                    response = await createFlag(flagParams);
                } else if (mode === 'edit') {
                    flagParams.id = flag.id;
                    response = await updateFlag(flagParams);
                }
            } else if (mode === 'delete') {
                response = await deleteFlag({ id: flag.id });
            }

            const responseValid = checkResponse(response, mounted, setStatus);
            if (!responseValid && mounted.current) {
                setSubmitting(false);
                return;
            }

            if (mounted.current) setSubmitted(true);
            setTimeout(() => handleClose(true), 1000);
        }
        submit();
    }, [mode, handleClose, flag, createFlag, updateFlag, deleteFlag, isReadingPlex]);

    return (
        <Modal show={showModal} onHide={handleClose}>
            <Modal.Header>
                <h2>Flag Form</h2>
            </Modal.Header>
            <FlagsBodyFooter
                mode={mode}
                selectedFlag={flag}
                studentOptions={studentOptions}
                userOptions={userOptions}
                centerOptions={centerOptions}
                employeeOptions={employeeOptions}
                loaded={loaded}
                submitted={submitted}
                handleClose={handleClose}
                handleSubmit={handleSubmit}
            />
        </Modal>
    );
}

export default connect(null, {
    fetchFlagsIds,
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchAdminUsersIds,
    fetchAdminUsersAll,
    fetchStudentsAll,
    createFlag,
    updateFlag,
    deleteFlag
})(FlagModal);